// src/components/lead-tracker/LeadTrackerTab.jsx
// Henry's coach's monthly Lead Tracker, derived LIVE from the pipelines.
// Visibility gated upstream in main-settings.jsx AND guarded here. Shared
// users (features.leadTracker) see the OWNER's tracker; only the owner edits
// goals / sharing / source mapping.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Lock, Target, Users, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { isLeadTrackerEnabled } from "../../utils/featureFlags";
import { isSuperAdminOwner } from "../../utils/brokerageSettings";
import {
  getAlldealforAdmin,
  listenBuyerLeads,
  listenListingLeads,
  getContactsByIds,
  getSourceLead,
} from "../../utils/firebasefunctions";
import {
  deriveTrackerRows,
  monthMetrics,
  goalsForYear,
  TRACKER_BUCKETS,
  UNASSIGNED_BUCKET,
} from "../../utils/leadTracker";
import {
  loadTrackerSettings,
  loadTrackerOverrides,
  saveTrackerOverride,
} from "../../utils/leadTrackerStore";
import { exportLeadTrackerExcel } from "../../utils/leadTrackerExcel";
import {
  buildPdfModel,
  downloadLeadTrackerPdf,
  buildYearPdfModel,
  downloadLeadTrackerYearPdf,
} from "../../utils/leadTrackerPdf";
import LeadTrackerTable from "./LeadTrackerTable";
import LeadTrackerYear from "./LeadTrackerYear";
import EditGoalsDialog from "./EditGoalsDialog";
import SharingDialog from "./SharingDialog";
import MapSourcesPanel from "./MapSourcesPanel";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December",
];

const pctText = (v) => (v == null ? "-" : `${Math.round(v * 100)}%`);

export default function LeadTrackerTab() {
  const [{ user, users, deals, buyerLeads, listingLeads, leadsource }, dispatch] =
    useStateValue();
  const isOwner = isSuperAdminOwner(user);
  const owner = useMemo(
    () => (users || []).find((u) => isSuperAdminOwner(u)) || (isOwner ? user : null),
    [users, user, isOwner],
  );
  const ownerId = owner?.id || "";
  const ownerName =
    [owner?.firstname, owner?.lastname].filter(Boolean).join(" ") ||
    owner?.name || "Team Lead";

  const now = new Date();
  const [view, setView] = useState("month");
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const [settings, setSettings] = useState({ goalsByYear: {} });
  const [overrides, setOverrides] = useState({});
  const [contactsById, setContactsById] = useState({});
  const [dialog, setDialog] = useState(""); // "" | "goals" | "sharing" | "sources"
  const [bucketFilter, setBucketFilter] = useState("");

  // Fresh data on open (status-sheet pattern): live lead listeners + one-shot
  // deals fetch. leadsource/users come from dashboard's mount fetches.
  useEffect(() => {
    let cancelled = false;
    getAlldealforAdmin("deals")
      .then((rows) => {
        if (!cancelled) dispatch({ type: actionType.STORE_DEALS, deals: rows });
      })
      .catch(() => {});
    const unsubListing = listenListingLeads((rows) =>
      dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows }),
    );
    const unsubBuyer = listenBuyerLeads((rows) =>
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows }),
    );
    loadTrackerOverrides().then((map) => {
      if (!cancelled) setOverrides(map);
    });
    return () => {
      cancelled = true;
      if (typeof unsubListing === "function") unsubListing();
      if (typeof unsubBuyer === "function") unsubBuyer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ownerId) return undefined;
    let cancelled = false;
    loadTrackerSettings(ownerId).then((s) => {
      if (!cancelled) setSettings(s);
    });
    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  // Contacts for the owner's leads (phone/email/referrer display) — by-id
  // fetch, chunk-safe, refreshed when the id set changes.
  const ownerLeadContactIds = useMemo(() => {
    const ids = new Set();
    [...(buyerLeads || []), ...(listingLeads || [])].forEach((l) => {
      if (String(l?.assignedAgent || "") === String(ownerId) && l?.contactId) {
        ids.add(l.contactId);
      }
    });
    return [...ids].sort();
  }, [buyerLeads, listingLeads, ownerId]);
  const idsKey = ownerLeadContactIds.join(",");
  useEffect(() => {
    if (!ownerLeadContactIds.length) return undefined;
    let cancelled = false;
    getContactsByIds(ownerLeadContactIds).then((list) => {
      if (cancelled) return;
      const map = {};
      (list || []).forEach((c) => {
        if (c) map[c.id] = c;
      });
      setContactsById(map);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const goals = useMemo(() => goalsForYear(settings, year), [settings, year]);
  const { rows, needsAttention } = useMemo(
    () =>
      deriveTrackerRows({
        buyerLeads,
        listingLeads,
        deals,
        contactsById,
        overridesById: overrides,
        systemSources: leadsource,
        ownerId,
      }),
    [buyerLeads, listingLeads, deals, contactsById, overrides, leadsource, ownerId],
  );
  const metrics = useMemo(
    () => monthMetrics(rows, year, monthIndex, goals),
    [rows, year, monthIndex, goals],
  );

  const unmappedCount = useMemo(
    () =>
      (leadsource || []).filter(
        (s) => !TRACKER_BUCKETS.some((b) => b.code === s.trackerBucket),
      ).length,
    [leadsource],
  );

  if (!isLeadTrackerEnabled({ user })) return null;
  if (!ownerId) {
    return <div className="text-sm text-slate-500 p-4">Loading team data...</div>;
  }

  const flipOverride = async (row) => {
    const next =
      row.callOrApptOverridden ? null : row.callOrAppt === "appt" ? "call" : "appt";
    const prev = overrides;
    setOverrides({ ...overrides, [row.leadId]: { callOrAppt: next } });
    try {
      await saveTrackerOverride(row.leadId, { callOrAppt: next }, user?.id);
    } catch (e) {
      setOverrides(prev);
      toast.error("Couldn't save the override");
    }
  };

  const prevMonth = () => {
    if (monthIndex === 0) {
      setMonthIndex(11);
      setYear(year - 1);
    } else setMonthIndex(monthIndex - 1);
  };
  const nextMonth = () => {
    if (monthIndex === 11) {
      setMonthIndex(0);
      setYear(year + 1);
    } else setMonthIndex(monthIndex + 1);
  };

  const doExcel = async () => {
    try {
      const { truncatedMonths } = await exportLeadTrackerExcel({
        rows, goals, ownerName, year,
      });
      if (truncatedMonths.length) {
        toast.warn(
          `Over 50 leads in ${truncatedMonths.join(", ")} - the Excel shows the first 50 of those months. The screen and PDF always show everything.`,
        );
      }
    } catch (e) {
      toast.error(e.message || "Excel export failed");
    }
  };
  const doPdf = async () => {
    try {
      const model = buildPdfModel({ rows, metrics, goals, year, monthIndex, ownerName });
      await downloadLeadTrackerPdf(model, year);
    } catch (e) {
      toast.error(e.message || "PDF export failed");
    }
  };
  // Whole year in one PDF — every month's page + the annual summary, the same
  // shape as the Excel workbook (Henry, 2026-09-01).
  const doYearPdf = async () => {
    try {
      const model = buildYearPdfModel({ rows, goals, year, ownerName });
      await downloadLeadTrackerYearPdf(model, year, ownerName);
    } catch (e) {
      toast.error(e.message || "PDF export failed");
    }
  };

  const cards = [
    {
      label: "Leads",
      big: `${metrics.leads}`,
      small: `/ ${goals.monthlyLeadsGoal} goal`,
      good: metrics.leadsOverUnder >= 0,
      note:
        metrics.leadsOverUnder >= 0
          ? `On goal (+${metrics.leadsOverUnder})`
          : `${Math.abs(metrics.leadsOverUnder)} under goal`,
    },
    {
      label: "Lead to Appt",
      big: pctText(metrics.leadToApptPct),
      small: `/ ${pctText(goals.leadToApptPct)} goal`,
      good: (metrics.leadToApptPct || 0) >= goals.leadToApptPct,
      note: `${metrics.appts} of ${metrics.leads}`,
    },
    {
      label: "Appt to Contract",
      big: pctText(metrics.apptToContractPct),
      small: `/ ${pctText(goals.apptToContractPct)} goal`,
      good: (metrics.apptToContractPct || 0) >= goals.apptToContractPct,
      note: `${metrics.contracts} contracts`,
    },
    {
      label: "Closings",
      big: `${metrics.closings}`,
      small: `/ ${goals.closingsGoal} goal`,
      good: metrics.closingsOverUnder >= 0,
      note:
        metrics.closingsOverUnder >= 0
          ? "On goal"
          : `${Math.abs(metrics.closingsOverUnder)} under goal`,
    },
    {
      label: "Mix",
      big: `${metrics.buyers}B / ${metrics.sellers}S`,
      small: "",
      good: true,
      note: `${metrics.preApproved} pre-approved`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-bold text-slate-900">Lead Tracker</h2>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
          <Lock className="w-3 h-3" />
          {isOwner ? "Yours, shared by toggle" : `Viewing ${ownerName}'s tracker`}
        </span>
        <div className="flex border border-slate-300 rounded-lg overflow-hidden text-xs ml-2">
          <button
            className={`px-3 py-1.5 ${view === "month" ? "bg-slate-800 text-white" : "text-slate-600"}`}
            onClick={() => setView("month")}
          >
            Month
          </button>
          <button
            className={`px-3 py-1.5 ${view === "year" ? "bg-slate-800 text-white" : "text-slate-600"}`}
            onClick={() => setView("year")}
          >
            Year
          </button>
        </div>
        {view === "month" ? (
          <div className="flex items-center gap-1 text-sm">
            <button className="p-1 hover:bg-slate-100 rounded" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-semibold text-slate-800 w-36 text-center">
              {MONTH_NAMES[monthIndex]} {year}
            </span>
            <button className="p-1 hover:bg-slate-100 rounded" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-sm">
            <button className="p-1 hover:bg-slate-100 rounded" onClick={() => setYear(year - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-semibold text-slate-800 w-16 text-center">{year}</span>
            <button className="p-1 hover:bg-slate-100 rounded" onClick={() => setYear(year + 1)}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex-1" />
        {isOwner && (
          <>
            <button
              className="inline-flex items-center gap-1 text-xs border border-slate-300 rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-50"
              onClick={() => setDialog("goals")}
            >
              <Target className="w-3.5 h-3.5" /> Edit Goals
            </button>
            <button
              className="inline-flex items-center gap-1 text-xs border border-slate-300 rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-50"
              onClick={() => setDialog("sharing")}
            >
              <Users className="w-3.5 h-3.5" /> Sharing
            </button>
          </>
        )}
        <button
          className="inline-flex items-center gap-1 text-xs rounded-lg px-3 py-1.5 text-white bg-slate-800 hover:bg-slate-700"
          onClick={doPdf}
        >
          <Download className="w-3.5 h-3.5" /> Month PDF
        </button>
        <button
          className="inline-flex items-center gap-1 text-xs rounded-lg px-3 py-1.5 text-white bg-slate-800 hover:bg-slate-700"
          onClick={doYearPdf}
        >
          <Download className="w-3.5 h-3.5" /> Year PDF
        </button>
        <button
          className="inline-flex items-center gap-1 text-xs rounded-lg px-3 py-1.5 text-white bg-emerald-700 hover:bg-emerald-600"
          onClick={doExcel}
        >
          <Download className="w-3.5 h-3.5" /> Excel
        </button>
      </div>

      {view === "month" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {cards.map((c) => (
              <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">
                  {c.label}
                </div>
                <div className="text-xl font-extrabold text-slate-900">
                  {c.big}{" "}
                  <span className="text-xs font-medium text-slate-500">{c.small}</span>
                </div>
                <div className={`text-[11px] font-semibold ${c.good ? "text-emerald-600" : "text-red-600"}`}>
                  {c.note}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-1.5 flex-wrap text-[11px]">
            {TRACKER_BUCKETS.map((b) => (
              <button
                key={b.code}
                onClick={() => setBucketFilter(bucketFilter === b.code ? "" : b.code)}
                className={`rounded-full border px-2.5 py-1 ${
                  bucketFilter === b.code
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-200"
                }`}
              >
                <b>{b.code}</b> {b.label} · {metrics.bucketCounts[b.code] || 0}
              </button>
            ))}
            <button
              onClick={() =>
                isOwner
                  ? setDialog("sources")
                  : setBucketFilter(bucketFilter === UNASSIGNED_BUCKET ? "" : UNASSIGNED_BUCKET)
              }
              className={`rounded-full border border-dashed px-2.5 py-1 ${
                (metrics.bucketCounts[UNASSIGNED_BUCKET] || 0) > 0 || unmappedCount > 0
                  ? "bg-rose-50 text-rose-700 border-rose-300"
                  : "bg-white text-slate-400 border-slate-200"
              }`}
            >
              {unmappedCount > 0
                ? `${unmappedCount} unmapped sources - fix`
                : `Unassigned · ${metrics.bucketCounts[UNASSIGNED_BUCKET] || 0}`}
            </button>
          </div>

          {needsAttention.length > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {needsAttention.length} lead(s) have no created date and are not
              shown in any month.
            </div>
          )}

          <LeadTrackerTable
            rows={rows}
            year={year}
            monthIndex={monthIndex}
            bucketFilter={bucketFilter}
            onFlipCallAppt={flipOverride}
            onReferrerSaved={(contactId, ref) =>
              setContactsById({
                ...contactsById,
                [contactId]: {
                  ...(contactsById[contactId] || { id: contactId }),
                  referredByContactId: ref ? ref.contactId : "",
                  referredByName: ref ? ref.name : "",
                },
              })
            }
          />
        </>
      )}

      {view === "year" && <LeadTrackerYear rows={rows} year={year} goals={goals} />}

      {dialog === "goals" && (
        <EditGoalsDialog
          year={year}
          goals={goals}
          ownerId={ownerId}
          onClose={() => setDialog("")}
          onSaved={(nextGoals) =>
            setSettings({
              ...settings,
              goalsByYear: { ...(settings.goalsByYear || {}), [String(year)]: nextGoals },
            })
          }
        />
      )}
      {dialog === "sharing" && <SharingDialog onClose={() => setDialog("")} />}
      {dialog === "sources" && (
        <MapSourcesPanel
          onClose={() => setDialog("")}
          onSaved={() =>
            getSourceLead().then((data) =>
              dispatch({ type: actionType.STORE_LEADS, payload: data }),
            )
          }
        />
      )}
    </div>
  );
}
